import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { PlayerScaffold, PlayerFooter } from '@/components/player/PlayerScaffold';
import { TabStrip, type Tab } from '@/components/player/TabStrip';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { EducateUnit } from '@/utils/units';
import type { CaseStudyContent, EducateInteraction } from '@/types/educate';
import { loadPlayerProgress, savePlayerProgress } from '@/utils/playerProgress';

interface CaseStudySaved {
  analyses: Record<string, string>;
  compared: string[];
}

export function CaseStudyPlayer({ module }: { module: EducateModule }) {
  const { storyline, units, currentUnit, currentIndex, completedUnits, advance, goBack, goTo } =
    module;
  const content = storyline?.content as CaseStudyContent | undefined;
  const showLogo = storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true;
  const total = units.length;

  const moments = units.filter((unit) => unit.kind === 'casePage');
  const momentCount = moments.length;
  const momentNumber = (unit: EducateUnit) => moments.findIndex((m) => m.id === unit.id) + 1;

  const saved = useMemo(
    () => loadPlayerProgress<CaseStudySaved>(storyline?.storylineId),
    [storyline?.storylineId]
  );
  const [analyses, setAnalyses] = useState<Record<string, string>>(() =>
    saved.analyses && typeof saved.analyses === 'object' ? saved.analyses : {}
  );
  const [compared, setCompared] = useState<Set<string>>(
    () => new Set(Array.isArray(saved.compared) ? saved.compared : [])
  );

  useEffect(() => {
    savePlayerProgress<CaseStudySaved>(storyline?.storylineId, {
      analyses,
      compared: [...compared]
    });
  }, [storyline?.storylineId, analyses, compared]);

  const tabs: Tab[] = units.map((unit, index) => ({
    id: unit.id,
    label: unit.label,
    state: index === currentIndex ? 'active' : completedUnits.includes(unit.id) ? 'done' : 'default'
  }));

  if (!content || !currentUnit) return null;

  const isLast = currentIndex === total - 1;

  const MIN_ANALYSIS_CHARS = 60;
  const isCasePage = currentUnit.kind === 'casePage';
  const analysisText = analyses[currentUnit.id]?.trim() ?? '';
  const enoughAnalysis = analysisText.length >= MIN_ANALYSIS_CHARS;
  const hasCompared = compared.has(currentUnit.id);
  const canCompare = enoughAnalysis && !hasCompared;
  const canAdvance = isCasePage ? enoughAnalysis && hasCompared : true;

  const handleNext = () => {
    if (!canAdvance) return;
    const text = analyses[currentUnit.id]?.trim();
    const interactions: EducateInteraction[] =
      isCasePage && text ? [{ unitId: currentUnit.id, kind: 'reflection', answer: text }] : [];
    advance(interactions);
  };

  return (
    <PlayerScaffold
      eyebrow="Case Study"
      title={storyline?.uiContent.title ?? ''}
      showLogo={showLogo}
      setting={content.setting}
      cast={content.cast}
      bodyTestId="case-study-screen"
      nav={<TabStrip tabs={tabs} onSelect={goTo} />}
      footer={
        <PlayerFooter
          left={
            <button
              type="button"
              onClick={goBack}
              disabled={currentIndex <= 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:opacity-40"
            >
              <ArrowLeft size={16} /> Previous
            </button>
          }
          center={
            <>
              {isCasePage && !enoughAnalysis
                ? `Write at least ${MIN_ANALYSIS_CHARS} characters to continue · `
                : isCasePage && !hasCompared
                  ? 'Compare with the expert reading to continue · '
                  : ''}
              Chapter {currentIndex + 1} of {total}
              {momentCount > 0 && ` · ${compared.size}/${momentCount} analyses compared`}
            </>
          }
          right={
            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-40"
            >
              {isLast ? 'Finish' : 'Next chapter'}
              {!isLast && <ArrowRight size={16} />}
            </button>
          }
        />
      }
    >
      <Chapter
        unit={currentUnit}
        momentNumber={momentNumber(currentUnit)}
        momentCount={momentCount}
        analysis={analyses[currentUnit.id] ?? ''}
        revealed={hasCompared}
        canCompare={canCompare}
        minChars={MIN_ANALYSIS_CHARS}
        onAnalysisChange={(text) => setAnalyses((prev) => ({ ...prev, [currentUnit.id]: text }))}
        onCompare={() => canCompare && setCompared((prev) => new Set(prev).add(currentUnit.id))}
      />
    </PlayerScaffold>
  );
}

interface ChapterProps {
  unit: EducateUnit;
  momentNumber: number;
  momentCount: number;
  analysis: string;
  revealed: boolean;
  canCompare: boolean;
  minChars: number;
  onAnalysisChange: (text: string) => void;
  onCompare: () => void;
}

function Chapter({
  unit,
  momentNumber,
  momentCount,
  analysis,
  revealed,
  canCompare,
  minChars,
  onAnalysisChange,
  onCompare
}: ChapterProps) {
  if (unit.kind === 'prose') {
    const isBrief = unit.id === 'brief';
    return (
      <article>
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">
          {isBrief ? 'Before you begin' : 'Wrapping up'}
        </p>
        <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
          {isBrief ? 'Case brief' : 'Synthesis'}
        </p>
        <h2 className="mt-2 text-2xl font-bold text-slate-900">{unit.title}</h2>
        <div className="mt-5 rounded-xl border-l-4 border-amber-400 bg-white p-6 shadow-sm">
          <p className="whitespace-pre-line text-lg leading-relaxed text-slate-700">{unit.body}</p>
        </div>
      </article>
    );
  }

  if (unit.kind !== 'casePage') return null;

  return (
    <article>
      {unit.stamp && (
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">{unit.stamp}</p>
      )}
      <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
        Moment {momentNumber} of {momentCount}
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">{unit.title}</h2>

      <div className="mt-5 rounded-xl border-l-4 border-amber-400 bg-white p-6 shadow-sm">
        <p className="whitespace-pre-line text-[15px] leading-relaxed text-slate-700">
          {unit.narrative}
        </p>
      </div>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
          Your analysis
        </p>
        <p className="mt-2 text-lg font-semibold text-slate-800">{unit.analysis}</p>

        <textarea
          value={analysis}
          onChange={(event) => onAnalysisChange(event.target.value)}
          rows={4}
          placeholder="Write your reading of this moment before you compare — two or three sentences is enough."
          className="mt-3 w-full resize-y rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-base text-slate-700 outline-none transition focus:border-brand-400 focus:bg-white"
        />

        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            {analysis.trim().length >= minChars
              ? 'Compare your reading with the expert before moving on.'
              : `Write your reading first — at least ${minChars} characters (${analysis.trim().length}/${minChars}).`}
          </p>
          <button
            type="button"
            onClick={onCompare}
            disabled={!canCompare}
            className={`shrink-0 rounded-lg px-3.5 py-2 text-xs font-semibold transition ${
              canCompare
                ? 'border border-brand-600 bg-brand-600 text-white hover:bg-brand-700'
                : 'border border-slate-300 text-slate-700 opacity-40'
            }`}
          >
            Compare with an expert reading
          </button>
        </div>

        {revealed && (
          <div className="mt-4 border-t-2 border-slate-200 pt-4">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">
              An expert reading
            </p>
            <p className="mt-2 whitespace-pre-line text-base leading-relaxed text-slate-700">
              {unit.modelAnswer}
            </p>
          </div>
        )}
      </div>
    </article>
  );
}
